import { Component, EventEmitter, Input, Output, inject, signal, OnChanges, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { LucideAngularModule, Globe } from 'lucide-angular';
import { Subject, takeUntil } from 'rxjs';

import { Project, ProjectType, Region, CertificationStandard } from '../../../../admin/models/project.model';
import { ContributeWizardService } from '../../services/contribute-wizard.service';
import { ProjectDetailModalComponent } from './project-detail-modal/project-detail-modal.component';

@Component({
  selector: 'app-step-choose-project',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    ButtonModule,
    CardModule,
    SelectModule,
    SkeletonModule,
    LucideAngularModule,
    ProjectDetailModalComponent,
  ],
  templateUrl: './step-choose-project.component.html',
  styles: [`
    .line-clamp-2 {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    /* Selected project button - dark green background */
    :host ::ng-deep .btn-selected {
      background-color: #2d5a3a !important;
      border-color: #2d5a3a !important;
      color: white !important;
    }

    :host ::ng-deep .btn-selected:hover {
      background-color: #234a2e !important;
      border-color: #234a2e !important;
    }
  `],
})
export class StepChooseProjectComponent implements OnChanges, OnInit, OnDestroy {
  @Input() projects: Project[] = [];
  @Input() loading = false;
  @Output() next = new EventEmitter<void>();

  protected wizardService = inject(ContributeWizardService);
  private transloco = inject(TranslocoService);
  private destroy$ = new Subject<void>();

  globeIcon = Globe;

  // Modal state
  showDetailModal = signal(false);
  detailProject = signal<Project | null>(null);

  selectedType: ProjectType | null = null;
  selectedRegion: Region | null = null;
  selectedCertification: CertificationStandard | null = null;

  // Options arrays (updated on language change)
  projectTypeOptions: { label: string; value: ProjectType }[] = [];
  regionOptions: { label: string; value: Region }[] = [];
  certificationOptions: { label: string; value: CertificationStandard }[] = [];

  filteredProjects = signal<Project[]>([]);

  ngOnInit(): void {
    this.updateOptions();
    // Update options when language changes
    this.transloco.langChanges$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateOptions();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(): void {
    this.updateOptions();
    this.applyFilters();
  }

  private updateOptions(): void {
    // Only show filter options that have matching projects
    const availableTypes = new Set(this.projects.map((p) => p.projectType));
    const availableRegions = new Set(this.projects.map((p) => p.region));
    const availableCerts = new Set(this.projects.map((p) => p.certificationStandard));

    this.projectTypeOptions = Object.values(ProjectType)
      .filter((type) => availableTypes.has(type))
      .map((type) => ({
        label: this.getProjectTypeLabel(type),
        value: type,
      }));
    this.regionOptions = Object.values(Region)
      .filter((region) => availableRegions.has(region))
      .map((region) => ({
        label: this.getRegionLabel(region),
        value: region,
      }));
    this.certificationOptions = Object.values(CertificationStandard)
      .filter((cert) => availableCerts.has(cert))
      .map((cert) => ({
        label: this.getCertificationLabel(cert),
        value: cert,
      }));
  }

  onFilterChange(): void {
    this.applyFilters();
  }

  private applyFilters(): void {
    let filtered = [...this.projects];

    if (this.selectedType) {
      filtered = filtered.filter((p) => p.projectType === this.selectedType);
    }
    if (this.selectedRegion) {
      filtered = filtered.filter((p) => p.region === this.selectedRegion);
    }
    if (this.selectedCertification) {
      filtered = filtered.filter((p) => p.certificationStandard === this.selectedCertification);
    }

    this.filteredProjects.set(filtered);
  }

  get hasActiveFilters(): boolean {
    return !!(this.selectedType || this.selectedRegion || this.selectedCertification);
  }

  clearFilters(): void {
    this.selectedType = null;
    this.selectedRegion = null;
    this.selectedCertification = null;
    this.applyFilters();
  }

  getProjectName(project: Project): string {
    const lang = this.transloco.getActiveLang().toUpperCase();
    if (lang === 'FR') return project.nameFR || project.nameEN;
    if (lang === 'NL') return project.nameNL || project.nameEN;
    return project.nameEN;
  }

  getProjectDescription(project: Project): string {
    const lang = this.transloco.getActiveLang().toUpperCase();
    if (lang === 'FR') return project.shortDescriptionFR || project.shortDescriptionEN;
    if (lang === 'NL') return project.shortDescriptionNL || project.shortDescriptionEN;
    return project.shortDescriptionEN;
  }

  getProjectTypeLabel(type: ProjectType): string {
    const key = `contribute.projectTypes.${type}`;
    const translated = this.transloco.translate(key);
    return translated !== key ? translated : type.replace(/_/g, ' ');
  }

  getRegionLabel(region: Region): string {
    const key = `contribute.regions.${region}`;
    const translated = this.transloco.translate(key);
    return translated !== key ? translated : region.replace(/_/g, ' ');
  }

  getCertificationLabel(cert: CertificationStandard): string {
    const key = `contribute.certifications.${cert}`;
    const translated = this.transloco.translate(key);
    return translated !== key ? translated : cert.replace(/_/g, ' ');
  }

  isSelected(project: Project): boolean {
    return this.wizardService.selectedProject()?.id === project.id;
  }

  selectProject(project: Project): void {
    this.wizardService.selectProject(project);
  }

  openDetailModal(project: Project, event: Event): void {
    event.stopPropagation();
    this.detailProject.set(project);
    this.showDetailModal.set(true);
  }

  onModalSelectProject(project: Project): void {
    this.selectProject(project);
  }

  onNext(): void {
    if (this.wizardService.canProceedStep1()) {
      this.next.emit();
    }
  }
}
